/**
 * Sensitive Data Configuration Types
 */

export interface SensitiveFieldConfig {
  /** Unique identifier for the field */
  id: string;
  /** Display label for the field */
  label: string;
  /** CSS selector to find elements containing this sensitive data */
  selector: string;
  /** Data attribute to mark elements (alternative to selector) */
  dataAttribute?: string;
  /** Whether this field is enabled for blurring */
  enabled: boolean;
  /** Category of sensitive data */
  category: 'personal' | 'financial' | 'contact' | 'identification' | 'other';
  /** Description of what this field contains */
  description?: string;
}

export interface SensitiveDataConfig {
  /** List of configured sensitive fields */
  fields: SensitiveFieldConfig[];
  /** Whether blurring is globally enabled */
  enabled: boolean;
  /** Blur intensity (0-10, where 10 is maximum blur) */
  blurIntensity: number;
}

/**
 * Preset sensitive fields based on current UI implementation
 */
export const PRESET_SENSITIVE_FIELDS: SensitiveFieldConfig[] = [
  // Customer Personal Information
  {
    id: 'customer-email',
    label: 'Customer Email',
    selector:
      '[data-field="email"], .customer-email, input[type="email"][id*="email"]',
    dataAttribute: 'data-sensitive-field="email"',
    enabled: true,
    category: 'contact',
    description: 'Customer email addresses',
  },
  {
    id: 'customer-phone',
    label: 'Customer Phone',
    selector:
      '[data-field="phone"], .customer-phone, input[type="tel"][id*="phone"]',
    dataAttribute: 'data-sensitive-field="phone"',
    enabled: true,
    category: 'contact',
    description: 'Customer phone numbers',
  },
  {
    id: 'customer-firstname',
    label: 'Customer First Name',
    selector: '[data-field="firstName"], .customer-firstname',
    dataAttribute: 'data-sensitive-field="firstName"',
    enabled: true,
    category: 'personal',
    description: 'Customer first names',
  },
  {
    id: 'customer-lastname',
    label: 'Customer Last Name',
    selector: '[data-field="lastName"], .customer-lastname',
    dataAttribute: 'data-sensitive-field="lastName"',
    enabled: true,
    category: 'personal',
    description: 'Customer last names',
  },
  {
    id: 'customer-address',
    label: 'Customer Address',
    selector: '[data-field="address"], .customer-address',
    dataAttribute: 'data-sensitive-field="address"',
    enabled: true,
    category: 'personal',
    description: 'Customer physical addresses',
  },
  {
    id: 'customer-birthday',
    label: 'Customer Birthday',
    selector: '[data-field="birthday"], .customer-birthday',
    dataAttribute: 'data-sensitive-field="birthday"',
    enabled: true,
    category: 'personal',
    description: 'Customer birth dates',
  },

  // Financial Information
  {
    id: 'payment-card',
    label: 'Payment Card Numbers',
    selector:
      '[data-field="cardNumber"], .payment-card, input[type="text"][id*="card"]',
    dataAttribute: 'data-sensitive-field="cardNumber"',
    enabled: true,
    category: 'financial',
    description: 'Credit/debit card numbers',
  },
  {
    id: 'payment-token',
    label: 'Payment Tokens',
    selector: '[data-field="paymentToken"], .payment-token',
    dataAttribute: 'data-sensitive-field="paymentToken"',
    enabled: true,
    category: 'financial',
    description: 'Payment gateway tokens',
  },
  {
    id: 'financial-amount',
    label: 'Financial Amounts',
    selector: '[data-field="amount"], .financial-amount, [data-amount]',
    dataAttribute: 'data-sensitive-field="amount"',
    enabled: true, // Enabled by default to protect financial data
    category: 'financial',
    description: 'Financial transaction amounts',
  },

  // User/Staff Information
  {
    id: 'user-email',
    label: 'User Email',
    selector: '[data-field="userEmail"], .user-email',
    dataAttribute: 'data-sensitive-field="userEmail"',
    enabled: true,
    category: 'contact',
    description: 'Staff/user email addresses',
  },
  {
    id: 'user-id',
    label: 'User ID',
    selector: '[data-field="userId"], .user-id, [data-user-id]',
    dataAttribute: 'data-sensitive-field="userId"',
    enabled: true,
    category: 'identification',
    description: 'User/staff identification numbers',
  },

  // Order Information
  {
    id: 'order-customer-info',
    label: 'Order Customer Information',
    selector: '[data-field="orderCustomer"], .order-customer',
    dataAttribute: 'data-sensitive-field="orderCustomer"',
    enabled: true,
    category: 'personal',
    description: 'Customer information in orders',
  },
];

/**
 * Default configuration
 */
export const DEFAULT_SENSITIVE_DATA_CONFIG: SensitiveDataConfig = {
  fields: PRESET_SENSITIVE_FIELDS,
  enabled: true,
  blurIntensity: 8,
};
